/**
 * The controls each tour step shows in the main area of the miniature.
 *
 * Only the real thing: the same components, class names and message keys the
 * screens use, with nothing wired and no data in them (the kit renders the
 * whole miniature inert). Which step lights which rail entry, and who the
 * miniature belongs to, is decided in `tour.ts`; this file is only the JSX.
 */
import {
  CopyableCode,
  Field,
  Panel,
  Select,
  SectionSplit,
  TableScroll,
  TextInput,
  kitText,
} from '@affdice/portal-kit';
import type { ReactNode } from 'react';

import { t, type AffiliateMessageKey } from './messages.ts';
import type { TourLayoutContent } from './tour.ts';

const noop = (): void => {};

/** The postback events the form offers, in its order (see `POSTBACK_EVENT_KEYS`). */
const EVENT_KEYS: readonly AffiliateMessageKey[] = [
  'postbacks.event.REGISTRATION',
  'postbacks.event.FTD',
  'postbacks.event.DEPOSIT',
  'postbacks.event.QUALIFICATION',
  'postbacks.event.WITHDRAWAL',
  'postbacks.event.WITHDRAWAL_REVERSED',
  'postbacks.event.REVENUE',
];

function links(): ReactNode {
  return (
    <Panel title={t('links.createModal')}>
      <Field label={t('link.field.campaign')}>
        <Select value="" onChange={noop} options={[]} />
      </Field>
      <Field label={t('link.field.label')}>
        <TextInput value="" onChange={noop} placeholder={t('link.field.label.placeholder')} />
      </Field>
      <Field label={t('link.field.extraLabel', { n: 2 })}>
        <TextInput value="" onChange={noop} />
      </Field>
      <div className="form-actions">
        <button className="primary" type="button" tabIndex={-1}>{t('link.create')}</button>
      </div>
    </Panel>
  );
}

function creatives(): ReactNode {
  return (
    <Panel title={t('creatives.panel')}>
      <div className="form-row">
        {(['creatives.filter.size', 'creatives.filter.type', 'creatives.filter.product',
          'creatives.filter.language', 'creatives.filter.geo'] as const).map((key) => (
          <Field key={key} label={t(key)}>
            <Select
              value=""
              onChange={noop}
              options={[{ value: '', label: t('creatives.filter.all') }]}
            />
          </Field>
        ))}
      </div>
      <Field label={t('linkTools.html')}>
        <CopyableCode text="" label={kitText('ui.copy')} />
      </Field>
    </Panel>
  );
}

function postbacks(): ReactNode {
  return (
    <Panel title={t('postbacks.panel')}>
      <Field label={t('postbacks.field.url')}>
        <TextInput value="" onChange={noop} placeholder="https://…?subid=REPLACE&payout=REPLACE" />
      </Field>
      <Field label={t('col.event')}>
        <Select
          value="REGISTRATION"
          onChange={noop}
          options={EVENT_KEYS.map((key) => ({ value: key.slice(key.lastIndexOf('.') + 1), label: t(key) }))}
        />
      </Field>
      <div className="form-actions">
        <button className="primary" type="button" tabIndex={-1}>{t('postbacks.register')}</button>
        <button type="button" tabIndex={-1}>{t('postbacks.action.sendTest')}</button>
      </div>
    </Panel>
  );
}

function reports(): ReactNode {
  return (
    <SectionSplit
      ariaLabel={t('nav.reports')}
      section="detailed"
      onSection={noop}
      groups={[
        { label: t('reports.group.activity'), items: [{ id: 'detailed', label: t('detailedActivity.title') }] },
        {
          label: t('reports.group.customer'),
          items: [
            { id: 'customer-activity', label: t('reports.customerActivity') },
            { id: 'customer-daily', label: t('reports.customerDaily') },
            { id: 'customer-registrations', label: t('reports.customerRegistrations') },
            { id: 'quarantine', label: t('quarantine.title') },
          ],
        },
        {
          label: t('reports.group.traffic'),
          items: [
            { id: 'clicks-impressions', label: t('clicksImpressions.title') },
            { id: 'device', label: t('reports.device') },
            { id: 'media', label: t('reports.media') },
            { id: 'countries', label: t('reports.countries') },
            { id: 'referrer', label: t('reports.referrer') },
            { id: 'clicks-log', label: t('reports.clicksLog') },
            { id: 'payload', label: t('reports.payload') },
            { id: 'funnel', label: t('reports.funnel') },
          ],
        },
      ]}
    >
      {null}
    </SectionSplit>
  );
}

function statements(): ReactNode {
  return (
    <Panel title={t('statements.caption')}>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">{t('col.period')}</th>
              <th scope="col">{t('col.statement')}</th>
              <th scope="col" className="num">{t('col.earned')}</th>
              <th scope="col" className="num">{t('col.held')}</th>
              <th scope="col" className="num">{t('col.payable')}</th>
              <th scope="col" className="num">{t('col.carriedForward')}</th>
              <th scope="col">{t('col.invoice')}</th>
            </tr>
          </thead>
        </table>
      </TableScroll>
    </Panel>
  );
}

/** Each step's screen title and controls; a step not listed shows the rail alone. */
export const tourMain: TourLayoutContent = (stepId) => {
  switch (stepId) {
    case 'welcome': return { title: t('dash.title') };
    case 'links': return { title: t('links.title'), content: links() };
    case 'creatives': return { title: t('links.title'), content: creatives() };
    case 'postbacks': return { title: t('nav.postbacks'), content: postbacks() };
    case 'reports': return { title: t('nav.reports'), content: reports() };
    case 'statements': return { title: t('nav.statements'), content: statements() };
    default: return {};
  }
};
